import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { Icon } from 'Components';
import { Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import Button from '../Button';
import classNames from 'classnames';
import { useStores } from 'Hooks/useStore';
import { ASSIGN_USERS_OPTION } from 'LearningManager/constants/createAssignment.constant';
import styles from './AssignmentActionFlowModal.module.scss';
import Modal from '../Modal/Modal';
import AssignmentUsersPicker from './AssignmentUsersPicker';
import { resolveAssignmentUserIds } from './resolveAssignmentUserIds';
import { resolveAssignmentAudience } from './resolveAssignmentAudience';
import AssignmentAudiencePicker, {
	AudienceSelection,
	EMPTY_AUDIENCE,
	hasAudienceSelection
} from './AssignmentAudiencePicker';
import Alert from 'PhishingManager/components/Alert';
import useLearnerCount from 'LearningManager/hooks/useLearnerCount';
import useTenantScope from 'LearningManager/hooks/useTenantScope';

export type ActionTarget = 'all' | 'specific';

// What the confirmation copy is allowed to state, counted rather than assumed.
export interface ActionSummary {
	entityCount: number;
	userCount: number;
}

export type ConfirmItems = string[] | ((summary: ActionSummary) => string[]);

type ModalStep = 'select' | 'confirm' | 'specific' | 'confirm-specific';

export interface ActionTargetOption {
	value: ActionTarget;
	title: string;
	description: string;
}

export interface ConfirmStepCopy {
	title: string;
	intro: string;
	items: ConfirmItems;
	actionLabel: string;
}

export interface SpecificStepCopy {
	title: string;
	description: string;
	actionLabel: string;
}

export interface SelectStepCopy {
	title: string;
	description: string;
	options: ActionTargetOption[];
	actionLabel: string;
}

interface AssignmentActionFlowModalProps {
	visible: boolean;
	assignmentId: number;
	scopes: string[];
	// The audience picker lets the user reach beyond the assignment's current
	// members; without it the step only lists those members.
	audience?: boolean;
	tenantId?: number;
	onCancel: () => void;
	onContinue: (
		target: ActionTarget,
		userIds?: string[]
	) => void | Promise<void>;
	select: SelectStepCopy;
	confirmAll: ConfirmStepCopy;
	specific: SpecificStepCopy;
	confirmSpecific: ConfirmStepCopy;
	isResetModal: boolean
}

interface ConfirmationViewProps {
	title: string;
	intro: string;
	items: string[];
	loading?: boolean;
	t: TFunction
}

const ConfirmationView: React.FC<ConfirmationViewProps> = ({
	title,
	intro,
	items,
	loading = false,
	t
}) => (
	<div className={styles.body}>
		<div className={styles.title}>{title}</div>

		<div className={styles.confirmContent}>
			<p className={styles.confirmText}>{intro}</p>
			{loading ? (
				<Icon type="loading" aria-label="Counting users" />
			) : (
				<ul className={styles.confirmList}>
					{items.map((item) => (
						<li key={item}>{item}</li>
					))}
				</ul>
			)}
		</div>

		<div className={styles.warning}>
			<Icon type="warningFilledIcon" role="img" />
			{t('learning.assignmentActionFlowModal.confirmationWarning')}
		</div>
	</div>
);

const AssignmentActionFlowModal: React.FC<AssignmentActionFlowModalProps> = observer(({
	visible,
	assignmentId,
	scopes,
	audience = false,
	tenantId,
	onCancel,
	onContinue,
	select,
	confirmAll,
	specific,
	confirmSpecific,
	isResetModal
}) => {
	const { learningManagerStore, assignUsersStore } = useStores();
	const [selected, setSelected] = useState<ActionTarget>('all');
	const [step, setStep] = useState<ModalStep>('select');
	const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
	const [audienceSelection, setAudienceSelection] = useState<AudienceSelection>(
		EMPTY_AUDIENCE
	);
	const [assignmentUserCount, setAssignmentUserCount] = useState<number>();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isResolving, setIsResolving] = useState(false);
	const [resolveMessage, setResolveMessage] = useState<string | null>(null);
	const { t } = useTranslation();
	const { globalTenantId } = useTenantScope();
	const { count, userIds: learnerIds, loading, getCount } = useLearnerCount(globalTenantId);

	// How many people the assignment reaches today — the "all users" confirmation
	// states it, so it is counted rather than guessed.
	const scopesKey = scopes.join(',');
	useEffect(() => {
		if (!visible || !assignmentId) return;
		let cancelled = false;
		setAssignmentUserCount(undefined);
		resolveAssignmentUserIds(
			learningManagerStore,
			assignUsersStore,
			assignmentId,
			scopesKey.split(','),
			tenantId
		)
			.then((userIds) => {
				if (!cancelled) setAssignmentUserCount(userIds.length);
			})
			.catch(() => undefined);
		return () => {
			cancelled = true;
		};
	}, [visible, assignmentId, scopesKey, tenantId, learningManagerStore, assignUsersStore]);

	const [isPrefilling, setIsPrefilling] = useState(false);
	useEffect(() => {
		if (!audience || !visible || !assignmentId) return;
		let cancelled = false;
		setIsPrefilling(true);
		resolveAssignmentAudience(
			learningManagerStore,
			assignUsersStore,
			assignmentId,
			scopesKey.split(','),
			tenantId
		)
			.then((prefill) => {
				if (cancelled) return;
				setAudienceSelection((prev) =>
					prev === EMPTY_AUDIENCE ? prefill : prev
				);
			})
			.catch(() => undefined)
			.finally(() => {
				if (!cancelled) setIsPrefilling(false);
			});
		return () => {
			cancelled = true;
		};
	}, [audience, visible, assignmentId, scopesKey, tenantId, learningManagerStore, assignUsersStore]);

	const resetState = () => {
		setSelected('all');
		setStep('select');
		setSelectedUserIds([]);
		setAudienceSelection(EMPTY_AUDIENCE);
		setResolveMessage(null);
	};

	const handleAudienceChange = (next: AudienceSelection) => {
		setResolveMessage(null);
		setAudienceSelection(next);
	};

	const handleCancel = () => {
		resetState();
		onCancel();
	};

	const toggleUser = (userId: string) => {
		setSelectedUserIds((prev) =>
			prev.includes(userId)
				? prev.filter((id) => id !== userId)
				: [...prev, userId]
		);
	};

	const successAction = async () => {
		// Both confirmation steps submit; the modal stays put until the caller
		// is done so the action can't be fired twice.
		const isSpecific = step === 'confirm-specific';
		const userIds = isSpecific ? learnerIds : undefined;
		setIsSubmitting(true);
		try {
			await onContinue(isSpecific ? 'specific' : 'all', userIds);
			resetState();
		} finally {
			setIsSubmitting(false);
		}
	}

	const handleAction = async () => {
		switch (step) {
			case 'select':
				const nextStep = selected === 'all' ? 'confirm' : 'specific';
				if(nextStep) await getCount('assigment', { assignment_source_ids: [assignmentId] }, isResetModal);
				setStep(nextStep);
				return;
			case 'specific': {
				if (!audience) {
					setStep('confirm-specific');
					return;
				}
				setIsResolving(true);
				setResolveMessage(null);
				try {
					let assignUserPayload = {};
					if(audienceSelection.methodId === ASSIGN_USERS_OPTION.ORGANIZATION) assignUserPayload = { group_labels: ['Organization'] };
					else if(audienceSelection.methodId === ASSIGN_USERS_OPTION.PROPERTY) assignUserPayload = { parent_group_ids: audienceSelection.entityIds };
					else if(audienceSelection.methodId === ASSIGN_USERS_OPTION.ROLE) assignUserPayload = { role_ids: audienceSelection.entityIds };
					else if(audienceSelection.methodId === ASSIGN_USERS_OPTION.GROUP) assignUserPayload = { group_ids: audienceSelection.entityIds };
					else if(audienceSelection.methodId === ASSIGN_USERS_OPTION.INDIVIDUAL) assignUserPayload = { user_ids: audienceSelection.userIds };
					const { count } = await getCount('assigment', { assignment_source_ids: [assignmentId], ...assignUserPayload }, isResetModal);

					// Nobody to act on — stay put rather than confirm a no-op.
					if (count) setStep('confirm-specific');
					else setResolveMessage(t('learning.assignmentActionFlowModal.errors.noUsersFound'));
				} catch {
					setSelectedUserIds([]);
					setResolveMessage(t('learning.assignmentActionFlowModal.errors.loadUsersFailed'));
				} finally {
					setIsResolving(false);
				}
				return;
			}
			default:
				break;
		}

		successAction();
	};

	const actionLabel =
		step === 'select'
			? select.actionLabel
			: step === 'confirm'
			? confirmAll.actionLabel
			: step === 'specific'
			? specific.actionLabel
			: confirmSpecific.actionLabel;

	const hasSpecificSelection = audience
		? hasAudienceSelection(audienceSelection)
		: selectedUserIds.length > 0;

	const isActionDisabled =
		isSubmitting ||
		isResolving ||
		(step === 'specific' && !hasSpecificSelection) ||
		(step === 'confirm' && count === 0) ||
		(step === 'confirm-specific' && count === 0);

	const confirmItems = (copy: ConfirmStepCopy, summary: ActionSummary) =>
		typeof copy.items === 'function' ? copy.items(summary) : copy.items;

	const renderBody = () => {
		switch (step) {
			case 'select':
				return (
					<div className={styles.body}>
						<div className={styles.title}>{select.title}</div>
						<p className={styles.description}>{select.description}</p>

						<div className={styles.options}>
							{select.options.map((option) => (
								<label
									key={option.value}
									className={classNames(styles.option, {
										[styles['option--selected']]:
											selected === option.value
									})}
									onClick={() => setSelected(option.value)}
								>
									<input
										type="radio"
										name="action-target"
										value={option.value}
										checked={selected === option.value}
										onChange={() =>
											setSelected(option.value)
										}
										className={styles.radioInput}
									/>
									<div className={styles.optionText}>
										<div className={styles.optionTitle}>
											{option.title}
										</div>
										<div className={styles.optionDesc}>
											{option.description}
										</div>
									</div>
								</label>
							))}
						</div>

						{selected === 'specific' ? <Alert title={t('learning.unassignScheduledModal.options.specific.warning_title')} subtitle={<Trans
							i18nKey="learning.unassignScheduledModal.options.specific.warning_message"
							components={{
								br: <br />,
								strong: <strong />
							}}
						/>} iconType='warningFilledIcon' /> : null}
					</div>
				);

			case 'confirm':
				return (
					<ConfirmationView
						t={t}
						title={confirmAll.title}
						intro={confirmAll.intro}
						items={confirmItems(confirmAll, {
							entityCount: 0,
							userCount: count ?? 0
						})}
						loading={loading}
					/>
				);

			case 'confirm-specific':
				return (
					<ConfirmationView
						t={t}
						title={confirmSpecific.title}
						intro={confirmSpecific.intro}
						items={confirmItems(confirmSpecific, {
							entityCount:
								audienceSelection.methodId ===
								ASSIGN_USERS_OPTION.INDIVIDUAL
									? 0
									: audienceSelection.entityIds.length,
							userCount: count
						})}
					/>
				);

			case 'specific':
				return (
					<div className={styles.body}>
						<div className={styles.title}>{specific.title}</div>
						<p className={styles.specificSubtitle}>
							{specific.description}
						</p>

						{audience && isPrefilling ? (
							<div className={styles.pickerLoading}>
								<Spin />
							</div>
						) : audience ? (
							<AssignmentAudiencePicker
								assignmentId={assignmentId}
								scopes={scopes}
								tenantId={tenantId}
								value={audienceSelection}
								onChange={handleAudienceChange}
							/>
						) : (
							<AssignmentUsersPicker
								assignmentId={assignmentId}
								scopes={scopes}
								tenantId={tenantId}
								selectedUserIds={selectedUserIds}
								onToggle={toggleUser}
							/>
						)}

						{resolveMessage && (
							<div
								className={classNames(
									styles.warning,
									styles.resolveMessage
								)}
								role="alert"
							>
								<Icon type="warningFilledIcon" role="img" />
								{resolveMessage}
							</div>
						)}

						{isResolving && (
							<div className={styles.pickerLoading}>
								<Spin />
							</div>
						)}
					</div>
				);
		}
	};

	return (
		<Modal
			visible={visible}
			onCancel={handleCancel}
			className={styles.root}
			centered
			size={step === 'specific' ? 'lg' : 'xs'}
			footer={
				<div className={styles.footer}>
					<Button
						variant="borderless"
						size="small"
						onClick={handleCancel}
						disabled={isSubmitting}
					>
						{t('learning.assignmentActionFlowModal.cancelBtn')}
					</Button>
					<Button
						size="small"
						type="primary"
						onClick={handleAction}
						disabled={isActionDisabled}
						loading={isSubmitting || isResolving}
					>
						{actionLabel}
					</Button>
				</div>
			}
		>
			{renderBody()}
		</Modal>
	);
});

export default AssignmentActionFlowModal;